import { Uuid } from "@/domains/global/identifiers";
import { PusherDomainPrefix, PusherServicePrefix } from "@/domains/pusher/constants";
import { objectModule } from "@/modules/object";
import { validationModule } from "@/modules/validation";
import { SerializedSyncOperation, SyncOperationGeneric } from "@/store/sync/operations/types";
import { liveQuery } from "dexie";
import { JsonObject } from "type-fest";

export function generateSyncActionSpaceScopedPusherChannelKey({ spaceId }: { spaceId: Uuid }) {
  const uppercaseSpaceId = spaceId.toUpperCase();
  return `${PusherServicePrefix.BACKEND_API}@${PusherDomainPrefix.SPACE_SCOPED_SYNC_UPDATE}@${uppercaseSpaceId}`;
}

export function generateSyncActionCollectionScopedPusherChannelKey({
  collectionId,
}: {
  collectionId: Uuid;
}) {
  const uppercaseCollectionId = collectionId.toUpperCase();
  return `${PusherServicePrefix.BACKEND_API}@${PusherDomainPrefix.COLLECTION_SCOPED_SYNC_UPDATE}@${uppercaseCollectionId}`;
}

export function generateSyncActionSpaceAccountScopedPusherChannelKey({
  spaceAccountId,
}: {
  spaceAccountId: Uuid;
}) {
  const uppercaseSpaceAccountId = spaceAccountId.toUpperCase();
  return `${PusherServicePrefix.BACKEND_API}@${PusherDomainPrefix.SPACE_ACCOUNT_SCOPED_SYNC_UPDATE}@${uppercaseSpaceAccountId}`;
}

export function generateSyncActionChatConversationScopedPusherChannelKey({
  chatConversationId,
}: {
  chatConversationId: Uuid;
}) {
  const uppercaseChatConversationId = chatConversationId.toUpperCase();
  return `${PusherServicePrefix.BACKEND_API}@${PusherDomainPrefix.CHAT_CONVERSATION_SCOPED_SYNC_UPDATE}@${uppercaseChatConversationId}`;
}

export function generateChatConversationLiveSyncUpdatePusherChannelKey({
  chatConversationId,
}: {
  chatConversationId: string;
}) {
  const uppercaseChatConversationId = chatConversationId.toUpperCase();
  return `${PusherServicePrefix.AUTHENTICATED_BACKEND_API}@${PusherDomainPrefix.CHAT_CONVERSATION_LIVE_SYNC_UPDATE}@${uppercaseChatConversationId}`;
}

export function generateSpaceAccountNoteLiveSyncUpdatePusherChannelKey({
  spaceAccountNoteId,
}: {
  spaceAccountNoteId: string;
}) {
  const uppercaseSpaceAccountNoteId = spaceAccountNoteId.toUpperCase();
  return `${PusherServicePrefix.AUTHENTICATED_BACKEND_API}@${PusherDomainPrefix.SPACE_ACCOUNT_NOTE_LIVE_SYNC_UPDATE}@${uppercaseSpaceAccountNoteId}`;
}

export function standardizeSyncId(syncId: string | undefined | null) {
  if (!syncId) {
    return undefined;
  }

  /**
   * These are our modern "CVR-style" sync IDs.
   * We shouldn't be doing any "standardization" of them.
   */
  if (syncId.startsWith("SYNC__")) {
    return syncId;
  }

  /**
   * Legacy logic for parsing out sync event ids from pre-CVR sync updates.
   *
   * TODO: @MacroMackie follow up with this on Monday, Nov 11.
   */
  const [prefix, uuid] = syncId.split("::");
  if (prefix !== "SYNC") throw new Error(`Invalid sync id: ${syncId}`);
  if (!validationModule.isUuid(uuid)) throw new Error(`Invalid sync id: ${syncId}`);
  return `${prefix}::${uuid.toUpperCase()}`;
}

export function getLoggableOperation(operation?: SyncOperationGeneric) {
  const { ...o } = { ...operation } as unknown as Record<string, unknown>;
  delete o.store;
  o.operationKind = operation?.operationKind || "<missing>";
  return objectModule.safeAsJson(o);
}

export function formatSyncOperation(
  syncOperation?: SyncOperationGeneric | SerializedSyncOperation
): JsonObject | null {
  if (!syncOperation) return null;

  return {
    operation_id: `${syncOperation.operationId}`,
    operation_kind: `${syncOperation.operationKind}`,
    payload: syncOperation.payload as JsonObject,
  };
}

export function saveJsonToFile(obj: object, filename: string) {
  const blob = new Blob([JSON.stringify(obj, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * @param querier - A function that checks for an expectedupdate from Dexie.
 * @param timeout - The timeout in milliseconds.
 * @param extraWait - The extra wait in milliseconds after querier succeeds.
 * @returns A promise that resolves when the querier succeeds.
 */
export const waitForLiveUpdate = (
  querier: () => boolean | Promise<boolean>,
  timeout = 2_500,
  extraWait = 250
) => {
  return new Promise((resolve, reject) => {
    const subscription = liveQuery(querier).subscribe({
      next: result => {
        if (result) {
          subscription.unsubscribe();
          setTimeout(() => resolve(void 0), extraWait);
        }
      },
      error: error => {
        subscription.unsubscribe();
        reject(error);
      },
      complete: () => {
        subscription.unsubscribe();
        reject(new Error("Live query completed without returning a result"));
      },
    });
    setTimeout(() => {
      subscription.unsubscribe();
      reject(new Error("Live query timed out"));
    }, timeout);
  });
};
